import { IStoreProps } from '../index';
import React, { MouseEvent, useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { CLOSE_HEADER_ACCOUNT_EVENT } from '../../account/ui/main';
import { CLOSE_SEARCH_D_EVENT } from '../../search-desktop/ui/main';
import { CLOSE_MINI_CART_EVENT, CLOSE_NOTIFICATIONS_EVENT } from '../../top-minicart/ui/main';
import { sendGAEvent } from '../../../../domain/marketing/gtm-provider';
import NotificationsPanel from './drop-panel';
import { mod } from '../../../../domain/math-provider';
import Divider from '../../shared/divider';
import BellIcon from '../../../../ui/svg/common/bell-icon';

interface INotificationsProps {
    isMobile: boolean;
    storeProps: IStoreProps;
}

const Notifications = ({ isMobile, storeProps }: INotificationsProps) => {

    const { useAppSelector, rootActions } = storeProps;

    const dispatch = useDispatch();

    const isOpened = useAppSelector(store => store.root.isOpened);
    const count = useAppSelector(store => store.root.count);
    const notifications = useAppSelector(store => store.root.notifications);
    const selectedItemGuid = useAppSelector(store => store.root.selectedItemGuid);

    // ------------------ OPEN / CLOSE ----------------------
    const toggle = () => {
        dispatch(rootActions.main({
            isOpened: !isOpened,
            isLoading: true,
        }));

        // close other popups
        document.dispatchEvent(new CustomEvent(CLOSE_SEARCH_D_EVENT));
        document.dispatchEvent(new CustomEvent(CLOSE_HEADER_ACCOUNT_EVENT));
        document.dispatchEvent(new CustomEvent(CLOSE_MINI_CART_EVENT));

        if(!isOpened){
            sendGAEvent('Notifications', 'Open Panel');
        }
    };

    const close = useCallback(() => {
        dispatch(
            rootActions.main({
                isOpened: false,
                isLoading: true,
                additionalOpenedMenuNotification: null,
                additionalOpenedMenuStyles: null,
            })
        )
    }, [dispatch, rootActions]);

    /**
     * handle click outside
     */
    useEffect(() => {

        document.addEventListener('click', close);

        return () => {
            document.removeEventListener('click', close);
        };
    }, [close]);

    /**
     * Close from outside.
     */
    useEffect(() => {
        document.addEventListener(CLOSE_NOTIFICATIONS_EVENT, close);

        return () => {
            document.removeEventListener(CLOSE_NOTIFICATIONS_EVENT, close);
        };
    }, [dispatch, close]);

    const stopPropagation = (evt: MouseEvent) => {
        evt.stopPropagation();
    };

    useEffect(() => {

        const getNextGuid = (forward: boolean) => {
            if(notifications.length <= 0) return null;

            if(!selectedItemGuid) return notifications[0].userNTFGUID;

            const index = notifications.findIndex(item => item.userNTFGUID === selectedItemGuid);
            if(index === -1) return notifications[0].userNTFGUID;

            const nexIndex = mod(forward ? index + 1 : index - 1, notifications.length);

            return notifications[nexIndex].userNTFGUID;
        };

        const arrowDown = () => {
            const nextGuid = getNextGuid(true);
            if(!nextGuid) return;

            dispatch(
                rootActions.main({
                    selectedItemGuid: nextGuid,
                })
            );
        };

        const arrowUp = () => {
            const prevGuid = getNextGuid(false);
            if(!prevGuid) return;

            dispatch(
                rootActions.main({
                    selectedItemGuid: prevGuid,
                })
            );
        };

        const handleKeyDown = (evt: KeyboardEvent) => {

            if(!isOpened) return;

            if(evt.key === 'ArrowDown') {
                evt.preventDefault();
                evt.stopPropagation();
                arrowDown();
            }

            if(evt.key === 'ArrowUp') {
                evt.preventDefault();
                evt.stopPropagation();
                arrowUp();
            }
        };

        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [
        notifications,
        dispatch,
        rootActions,
        selectedItemGuid,
        isOpened,
    ]);

    return (
        <div className={ `flex items-center ${ isMobile ? 'justify-center h-full mr-1' : 'relative' }` } onClick={ stopPropagation }>

            { !isMobile &&  <Divider margin="mr-4" /> }

            <button
                type="button"
                onClick={ toggle }
                className="flex items-center justify-center relative mr-5">

                <span className="sr-only">Show notifications</span>

                {
                    count > 0 &&
                    <div
                        aria-hidden="true"
                        className="top-notifications-count absolute bg-blue-200 rounded-full flex items-center justify-center text-white"
                        style={{
                            background: '#E5484D',
                        }}>{ count }</div>
                }

                <BellIcon width="24px" height="24px" color="#fff" />

            </button>

            {
                isOpened &&
                <NotificationsPanel
                    storeProps={ storeProps }
                    isMobile={ isMobile }
                />
            }
        </div>
    )
};

export default Notifications;